import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { HERO } from "@/constants/testIds";
import { ASSETS, CONTACT } from "@/constants/site";
import { ArrowRight, Phone } from "lucide-react";

export default function Hero() {
  const { t } = useLang();
  return (
    <section id="top" data-testid={HERO.root} className="relative min-h-screen flex items-end overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src={ASSETS.kawasaki}
          alt="Kawasaki wheel loader"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 hero-vignette" />
      </div>

      {/* Vertical rail with brand strip */}
      <div className="hidden lg:flex absolute left-6 top-0 bottom-0 z-10 flex-col justify-between py-32 items-center">
        <div className="text-[10px] tracking-[0.4em] uppercase text-neutral-500 [writing-mode:vertical-rl] rotate-180">
          EST · DORAL · FLORIDA
        </div>
        <div className="w-px h-40 bg-white/10" />
        <div className="text-[10px] tracking-[0.4em] uppercase text-neutral-500 [writing-mode:vertical-rl] rotate-180">
          NAICS · 423830
        </div>
      </div>

      <div className="relative z-10 w-full max-w-[1600px] mx-auto px-6 md:px-12 pb-20 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl"
        >
          <div className="section-label mb-8">{t.hero.eyebrow}</div>

          <h1 className="font-heading text-white uppercase leading-[0.9] text-[16vw] sm:text-[13vw] md:text-[10vw] lg:text-[9rem] xl:text-[11rem]">
            <span className="block">{t.hero.titleLine1}</span>
            <span className="block text-[#F5B800]">{t.hero.titleLine2}</span>
          </h1>

          <p className="mt-8 max-w-2xl text-neutral-300 text-base md:text-lg leading-relaxed">
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a data-testid={HERO.ctaPrimary} href="#contact" className="btn-primary sharp">
              {t.hero.ctaPrimary}
              <ArrowRight size={16} strokeWidth={2.5} />
            </a>
            <a data-testid={HERO.ctaSecondary} href={CONTACT.phoneHref} className="btn-ghost sharp">
              <Phone size={15} strokeWidth={2.5} />
              {t.hero.ctaSecondary}
            </a>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="mt-20 grid grid-cols-3 gap-8 max-w-3xl border-t border-white/10 pt-8"
        >
          {[t.hero.stat1, t.hero.stat2, t.hero.stat3].map((s, i) => (
            <div key={i}>
              <div className="font-heading text-white text-3xl md:text-5xl">{s.k}</div>
              <div className="text-[10px] md:text-xs mt-1 uppercase tracking-[0.25em] text-neutral-500 font-bold">{s.v}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-6 right-6 md:right-12 z-10 text-[10px] tracking-[0.35em] uppercase text-neutral-500 hidden md:flex items-center gap-3">
        <div className="w-16 h-px bg-white/20" />
        Scroll
      </div>
    </section>
  );
}
